import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarNavItem from "./SidebarNavItem.jsx";
import SidebarRailTooltip from "./SidebarRailTooltip.jsx";
import SidebarCollapseIcon from "../Icons/sidebarCollapseIcon.jsx";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser, selectIsAuthenticated } from "../../store/slices/authSlice";
import { useGetSuggestionsQuery } from "../../store/api/kcApi";
import { ROUTES } from "../../constants/routes.js";
import { toTitleCaseName } from "../../utils/string.js";
import { isAdminRole } from "../Settings/connectionHelpers.js";


function DefaultProfilePopout({ user, onSignOut, onNavigate, onClose }) {
  function handleNavigate(path) {
    onNavigate?.(path);
    onClose?.();
  }
  // The hook is INSIDE this component so polling only runs while the
  // popout is mounted (open). Closing the popout unmounts this tree,
  // RTK Query unsubscribes, polling stops. ``limit: 1`` keeps the
  // payload cheap — only the ``count`` field is read.
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: suggestionsData } = useGetSuggestionsQuery(
    { limit: 1 },
    { skip: !isAuthenticated, pollingInterval: 60000 },
  );
  const suggestionsCount = suggestionsData?.count ?? 0;
  const kcCount =
    typeof suggestionsCount === "number" && suggestionsCount > 0
      ? suggestionsCount > 99
        ? "99+"
        : String(Math.floor(suggestionsCount))
      : null;
  const name = user?.name || user?.username || "User";
  const email = user?.email || "";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "U";

  return (
    <>
      <div className="profile-popout-head">
        <div className="profile-popout-avatar">{initials}</div>
        <div>
          <div className="profile-popout-name">{name}</div>
          <div className="profile-popout-email">{email}</div>
        </div>
      </div>
      <div className="profile-popout-menu">
        {/* <div className="profile-popout-item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          Profile &amp; Account
        </div> */}
        {/* <div className="profile-popout-item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          Notification Preferences
        </div> */}
        <div
          className="profile-popout-item"
          role="button"
          tabIndex={0}
          onClick={() => handleNavigate(ROUTES.KNOWLEDGE_CONFIG)}
          onKeyDown={(e) =>
            e.key === "Enter" && handleNavigate(ROUTES.KNOWLEDGE_CONFIG)
          }
          data-testid="popout-knowledge-config"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          <span style={{ flex: 1 }}>Knowledge Config</span>
          {kcCount ? (
            <span className="profile-popout-count" aria-label={`${suggestionsCount} pending`}>
              {kcCount}
            </span>
          ) : null}
        </div>
        {isAdminRole(user?.role) && (
        <div
          className="profile-popout-item"
          role="button"
          tabIndex={0}
          onClick={() => handleNavigate(ROUTES.SETTINGS)}
          onKeyDown={(e) => e.key === "Enter" && handleNavigate(ROUTES.SETTINGS)}
          data-testid="popout-db-config"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          DB Config
        </div>
        )}
        <div
          className="profile-popout-item"
          role="button"
          tabIndex={0}
          onClick={() => handleNavigate(ROUTES.NOTIFICATIONS)}
          onKeyDown={(e) =>
            e.key === "Enter" && handleNavigate(ROUTES.NOTIFICATIONS)
          }
          data-testid="popout-notifications"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          Notifications
        </div>
        {/* <div className="profile-popout-item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="4" height="6" rx="1" />
            <rect x="10" y="7" width="4" height="10" rx="1" />
            <rect x="17" y="3" width="4" height="14" rx="1" />
          </svg>
          Workspace Usage
        </div>
        <div className="profile-popout-item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          What's New
        </div> */}
      </div>
      {/* <div className="profile-popout-credits">
        <div className="profile-popout-credits-row">
          <span className="profile-popout-credits-label">Credits remaining</span>
          <span className="profile-popout-credits-count">
            3,400 <span style={{ color: "var(--text-3)", fontWeight: 400 }}>/ 5,000</span>
          </span>
        </div>
        <div className="profile-popout-credits-bar">
          <div className="profile-popout-credits-fill" style={{ width: "68%" }} />
        </div>
        <div className="profile-popout-credits-sub">Resets March 31, 2026</div>
      </div> */}
      <div className="profile-popout-sep" />
      <div className="profile-popout-menu" style={{ paddingBottom: 7 }}>
        <div className="profile-popout-item danger" role="button" tabIndex={0} onClick={onSignOut} onKeyDown={(e) => e.key === "Enter" && onSignOut()}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Sign out
        </div>
      </div>
    </>
  );
}

export default function SidebarFooter({
  footerItems,
  onNavigate,
  isCollapsed = false,
  onToggleCollapsed,
}) {
  const [showProfile, setShowProfile] = useState(false);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileWrapRef = useRef(null);
  const rawUserName = user?.username ?? "Unknown User";
  const userName = toTitleCaseName(rawUserName) || "Unknown User";
  const userRole = user?.role
    ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}`
    : "Member";
  const userInitials =
    userName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "U";

  useEffect(() => {
    if (!showProfile) return;
    const handleOutsideClick = (event) => {
      if (!profileWrapRef.current) return;
      if (!profileWrapRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showProfile]);

  const profileRow = (
    <div
      className="user-row"
      role="button"
      tabIndex={0}
      onClick={() => setShowProfile((prev) => !prev)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowProfile((prev) => !prev)}
      aria-label="Open profile menu"
      aria-expanded={showProfile}
    >
      <div className="user-avatar">{userInitials}</div>
      <div>
        <div className="user-name">{userName}</div>
        <div className="user-role">{userRole}</div>
      </div>
      <span
        style={{
          marginLeft: "auto",
          opacity: 0.35,
          flexShrink: 0,
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </span>
    </div>
  );

  return (
    <div className="sidebar-footer-stack">
      {footerItems.length > 0 ? (
        <div className="nav-group sidebar-footer-nav">
          {footerItems.map((item) =>
            item.onClick ? (
              <SidebarNavItem
                key={item.dataPage}
                as="button"
                active={item.active}
                disabled={item.disabled}
                label={item.label}
                icon={item.icon}
                badge={item.badge}
                count={item.count}
                dataPage={item.dataPage}
                collapsed={isCollapsed}
                onClick={item.onClick}
              />
            ) : (
              <SidebarNavItem
                key={item.dataPage}
                as="div"
                label={item.label}
                icon={item.icon}
                dataPage={item.dataPage}
                collapsed={isCollapsed}
              />
            )
          )}
        </div>
      ) : null}

      <div className="sidebar-footer" ref={profileWrapRef}>
        {showProfile && (
          <div className="profile-popout">
            <DefaultProfilePopout
              user={user}
              onSignOut={() => {
                dispatch(logout());
                setShowProfile(false);
                navigate(ROUTES.SIGN_IN, { replace: true });
              }}
              onNavigate={onNavigate}
              onClose={() => setShowProfile(false)}
            />
          </div>
        )}

        <div className="sidebar-footer-row">
          {isCollapsed ? (
            <SidebarRailTooltip text={`${userName} · Profile`}>{profileRow}</SidebarRailTooltip>
          ) : (
            profileRow
          )}
          {onToggleCollapsed ? (
            <button
              type="button"
              className="sidebar-collapse-inline-btn"
              onClick={onToggleCollapsed}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!isCollapsed}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <SidebarCollapseIcon size={14} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
